import { useContext, useState, useEffect, useCallback } from "react";
import { CurrencyContext } from "../context/CurrencyContext";
import { getCurrencyRate } from "../services/api";

const rateCache = new Map();
const pendingKeys = new Set();
// Every mounted useCurrencyFormatter() instance registers a listener here.
// A rate fetch is de-duped across instances via `pendingKeys` (only one
// network request per cache key), but every instance still needs to know
// when that shared fetch resolves — not just whichever instance happened
// to be the one that triggered it. Without this, an instance that merely
// observed the key as already-pending would show "…" forever, waiting on
// nothing, until some unrelated re-render happened to check the cache again.
const listeners = new Set();

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

const toDateKey = (date) => {
  const parsed = date ? new Date(date) : new Date();
  if (Number.isNaN(parsed.getTime())) return new Date().toISOString().slice(0, 10);
  return parsed.toISOString().slice(0, 10);
};

const INTL_FORMATTERS = {
  MYR: new Intl.NumberFormat("en-MY", { style: "currency", currency: "MYR" }),
  SGD: new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" }),
};

export const formatByCurrency = (amount, currencyCode) =>
  (INTL_FORMATTERS[currencyCode] || INTL_FORMATTERS.MYR).format(Number(amount) || 0);

const ensureRate = (fromCurrency, toCurrency, dateKey) => {
  const cacheKey = `${fromCurrency}|${toCurrency}|${dateKey}`;
  if (rateCache.has(cacheKey) || pendingKeys.has(cacheKey)) return;
  pendingKeys.add(cacheKey);

  getCurrencyRate(dateKey, toCurrency, fromCurrency)
    .then((response) => {
      rateCache.set(cacheKey, response?.data?.rate ?? null);
    })
    .catch(() => {
      rateCache.set(cacheKey, null);
    })
    .finally(() => {
      pendingKeys.delete(cacheKey);
      notifyListeners();
    });
};

export const useCurrencyFormatter = () => {
  const context = useContext(CurrencyContext);
  const currency = context?.currency || "SGD";
  const [, forceRender] = useState(0);

  useEffect(() => {
    const listener = () => forceRender((n) => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const format = useCallback(
    (amount, date, sourceCurrency = "MYR") => {
      if (sourceCurrency === currency) return formatByCurrency(amount, currency);

      const dateKey = toDateKey(date);
      const cacheKey = `${sourceCurrency}|${currency}|${dateKey}`;
      if (!rateCache.has(cacheKey)) {
        ensureRate(sourceCurrency, currency, dateKey);
        // Don't show the raw amount in sourceCurrency here — the caller
        // asked to see it in `currency`, and showing the unconverted
        // number (even briefly, until the rate arrives and this re-renders)
        // reads as a flash of the wrong value rather than a loading state.
        return "…";
      }

      const rate = rateCache.get(cacheKey);
      return rate
        ? formatByCurrency((Number(amount) || 0) * rate, currency)
        : formatByCurrency(amount, sourceCurrency);
    },
    [currency],
  );

  return { format, currency };
};
